import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import axiosInstance, { AuthNotReadyError } from "../utils/axiosInstance";
import { UIContext } from "./UIProvider";
import { JobProps } from "../types";
import {
  RegionProps,
  BonusProps,
  CompanyProps,
  CommunitiesProps,
  ProjectsProps,
} from "./types";
import { msalInstance } from "..";
import { idbGet, idbSet } from "../utils/idbCache";
import { useLoadCommunities } from "../hooks/useLoadCommunities";
import { useLoadBonuses } from "../hooks/useLoadBonuses";
import { useLoadJobs } from "../hooks/useLoadJobs";
import { useLoadProjects } from "../hooks/useLoadProjects";

// ── UI Dispatch type ──────────────────────────────────────────────────────────

type SnackbarSeverity = "error" | "warning" | "info" | "success";

interface SnackbarAction {
  type: "Snackbar";
  payload: {
    show: boolean;
    message: string;
    severity: SnackbarSeverity;
  };
}

type UIAction = SnackbarAction;
type UIDispatch = (action: UIAction) => void;

// ── Types ─────────────────────────────────────────────────────────────────────

interface DataContextType {
  regions: RegionProps[];
  bonuses: BonusProps[];
  isRegionLoading: boolean;
  isBonusTypeLoading: boolean;
  isRoleLoading: boolean;
  loadingBonuses: boolean;
  loadingCompanies: boolean;
  loadingCommunities: boolean;

  companies: CompanyProps[];
  communities: CommunitiesProps[];
  communities2: CommunitiesProps[];
  allJobs: JobProps[];
  projects: ProjectsProps[];

  selectedRegion: RegionProps | null;
  selectedCompany: CompanyProps | null;
  selectedCommunity: CommunitiesProps | null;
  selectedJob: JobProps | null;

  role: string | null;

  setRole: (role: string | null) => void;

  setSelectedRegion: (
    region:
      | RegionProps
      | null
      | ((prev: RegionProps | null) => RegionProps | null)
  ) => void;

  setSelectedCompany: (
    company:
      | CompanyProps
      | null
      | ((prev: CompanyProps | null) => CompanyProps | null)
  ) => void;

  setSelectedCommunity: (community: CommunitiesProps | null) => void;
  setSelectedJob: React.Dispatch<React.SetStateAction<JobProps | null>>;

  loadJobsByProject: () => void;

  loadBonuses: (role: string) => void;
  loadRegions: () => Promise<RegionProps[]>;
  loadCompanies: () => Promise<CompanyProps[]>;
  loadJobs: () => void;
  loadCommunities: (companyId: number, regionId: number) => void;
  loadProjects: () => void;
  updateSelectedRegion: (region: RegionProps) => void;
  loadDataForRole: (role: string) => void;

  bonusType: string;
  setBonusType: (type: string) => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const BONUS_TYPE_KEY = "cba.bonusType";
const ROLE_KEY = "cba.role";
const DEFAULT_BONUS_TYPE = "ClosingBonus";

// ── Storage helpers ───────────────────────────────────────────────────────────

const readFromStorageJson = <T,>(key: string, fallback: T): T => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? (JSON.parse(stored) as T) : fallback;
  } catch {
    return fallback;
  }
};

const readFromStorageString = (
  key: string,
  fallback: string | null = null
): string | null => localStorage.getItem(key) ?? fallback;

const readBonusType = (): string =>
  localStorage.getItem(BONUS_TYPE_KEY) || DEFAULT_BONUS_TYPE;

// ── Typed updater resolver (no any casts) ─────────────────────────────────────

type Updater<T> = T | null | ((prev: T | null) => T | null);

const isUpdaterFn = <T,>(v: Updater<T>): v is (prev: T | null) => T | null =>
  typeof v === "function";

const resolveUpdater = <T,>(next: Updater<T>, prev: T | null): T | null =>
  isUpdaterFn<T>(next) ? next(prev) : next;

// ── Context ───────────────────────────────────────────────────────────────────

const DataContext = createContext<DataContextType | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────────────────

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [, dispatch] = useContext(UIContext) as [unknown, UIDispatch];

  // In-flight locks (simple boolean locks per resource)
  const inFlightRef = useRef({
    regions: false,
    companies: false,
  });

  // Prevent double initial load for same role (React 18 StrictMode)
  const lastLoadedRoleRef = useRef<string | null>(null);

  // ── State ─────────────────────────────────────────────────────────────────

  const [isRegionLoading, setIsRegionLoading] = useState(false);
  const [isBonusTypeLoading, setIsBonusTypeLoading] = useState(false);
  const [isRoleLoading, setIsRoleLoading] = useState(false);
  const [bonuses, setBonuses] = useState<BonusProps[]>([]);
  const [loadingBonuses, setLoadingBonuses] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [role, _setRole] = useState<string | null>(() =>
    readFromStorageString(ROLE_KEY)
  );

  const [regions, setRegions] = useState<RegionProps[]>(() =>
    readFromStorageJson("cba.regions", [])
  );

  const [companies, setCompanies] = useState<CompanyProps[]>(() =>
    readFromStorageJson("cba.companies", [])
  );

  const [communities, setCommunities] = useState<CommunitiesProps[]>([]);
  const [communities2, setCommunities2] = useState<CommunitiesProps[]>([]);
  const [allJobs, setAllJobs] = useState<JobProps[]>([]);
  const [projects, setProjects] = useState<ProjectsProps[]>([]);
  const [bonusType, _setBonusType] = useState<string>(() => readBonusType());

  const [selectedRegion, _setSelectedRegion] = useState<RegionProps | null>(() =>
    readFromStorageJson("cba.selectedRegion", null)
  );
  const [selectedCompany, _setSelectedCompany] = useState<CompanyProps | null>(
    () => readFromStorageJson("cba.selectedCompany", null)
  );
  const [selectedCommunity, _setSelectedCommunity] =
    useState<CommunitiesProps | null>(() =>
      readFromStorageJson("cba.selectedCommunity", null)
    );
  const [selectedJob, setSelectedJob] = useState<JobProps | null>(null);

  const [isMsalReady, setIsMsalReady] = useState(() =>
    msalInstance.getAllAccounts().length > 0
  );

  // ── Snackbar helper ───────────────────────────────────────────────────────

  const showError = useCallback(
    (message: string) => {
      dispatch({
        type: "Snackbar",
        payload: { show: true, message, severity: "error" },
      });
    },
    [dispatch]
  );

  // ── Hooks ────────────────────────────────────────────────────────────────

  const { loadCommunities, isLoading: loadingCommunities, resetInFlight: resetCommunitiesInFlight } = useLoadCommunities(
    setCommunities,
    setCommunities2
  );
  const { loadBonuses } = useLoadBonuses(setBonuses, setLoadingBonuses, showError);
  const { loadJobs } = useLoadJobs(setAllJobs, showError);
  const { loadProjects } = useLoadProjects(setProjects);

  // ── Refs to avoid stale closures while keeping callbacks stable ────────────

  useEffect(() => {
    idbGet<JobProps[]>("cba.allJobs")
      .then((cached) => {
        if (Array.isArray(cached) && cached.length > 0) setAllJobs(cached);
      })
      .catch(() => {
        /* ignore */
      });
  }, []);

  const bonusTypeRef = useRef(bonusType);
  useEffect(() => {
    bonusTypeRef.current = bonusType;
  }, [bonusType]);

  const roleRef = useRef(role);
  useEffect(() => {
    roleRef.current = role;
  }, [role]);

  const companiesRef = useRef(companies);
  useEffect(() => {
    companiesRef.current = companies;
  }, [companies]);

  const selectedCompanyIdRef = useRef(selectedCompany?.companyId);
  const selectedRegionIdRef = useRef(selectedRegion?.regionId);

  useEffect(() => {
    selectedCompanyIdRef.current = selectedCompany?.companyId;
  }, [selectedCompany?.companyId]);

  useEffect(() => {
    selectedRegionIdRef.current = selectedRegion?.regionId;
  }, [selectedRegion?.regionId]);

  // ── MSAL ready detection ──────────────────────────────────────────────────

  useEffect(() => {
    if (isMsalReady) return;

    const callbackId = msalInstance.addEventCallback(() => {
      if (msalInstance.getAllAccounts().length > 0) {
        setIsMsalReady(true);
      }
    });

    return () => {
      if (callbackId) msalInstance.removeEventCallback(callbackId);
    };
  }, [isMsalReady]);

  // ── Wrapped persistent setters ────────────────────────────────────────────

  const setSelectedRegion = useCallback((next: Updater<RegionProps>) => {
    _setSelectedRegion((prev) => {
      const resolved = resolveUpdater(next, prev);
      if (resolved)
        localStorage.setItem("cba.selectedRegion", JSON.stringify(resolved));
      else localStorage.removeItem("cba.selectedRegion");
      return resolved;
    });
  }, []);

  const setSelectedCompany = useCallback((next: Updater<CompanyProps>) => {
    _setSelectedCompany((prev) => {
      const resolved = resolveUpdater(next, prev);
      if (resolved)
        localStorage.setItem("cba.selectedCompany", JSON.stringify(resolved));
      else localStorage.removeItem("cba.selectedCompany");
      return resolved;
    });
  }, []);

  const setSelectedCommunity = useCallback(
    (community: CommunitiesProps | null) => {
      _setSelectedCommunity(community);
      if (community)
        localStorage.setItem(
          "cba.selectedCommunity",
          JSON.stringify(community)
        );
      else localStorage.removeItem("cba.selectedCommunity");
    },
    []
  );

  // ── Loaders ───────────────────────────────────────────────────────────────

  const loadRegions = useCallback(async (): Promise<RegionProps[]> => {
    if (inFlightRef.current.regions) {
      // Return cached value so callers still get something usable
      return readFromStorageJson<RegionProps[]>("cba.regions", []);
    }
    inFlightRef.current.regions = true;

    try {
      const res = await axiosInstance.get("/api/cba/regions");
      const regionsData: RegionProps[] = (res.data || []).sort(
        (a: RegionProps, b: RegionProps) =>
          a.regionName.localeCompare(b.regionName)
      );

      setRegions(regionsData);
      localStorage.setItem("cba.regions", JSON.stringify(regionsData));
      setSelectedRegion((prev) => prev ?? regionsData[0] ?? null);
      return regionsData;
    } catch (e) {
      if (e instanceof AuthNotReadyError) {
        return readFromStorageJson<RegionProps[]>("cba.regions", []);
      }
      console.error("Error loading regions:", e);
      return readFromStorageJson<RegionProps[]>("cba.regions", []);
    } finally {
      inFlightRef.current.regions = false;
    }
  }, [setSelectedRegion]);

  const findCompanyByRegion = useCallback(
    (
      companiesList: CompanyProps[],
      regionId: number
    ): CompanyProps | undefined => {
      for (const company of companiesList) {
        const regionIds = (company.regionIds || "")
          .split(",")
          .map((x) => x.trim());
        if (regionIds.includes(regionId.toString())) return company;
      }
      return undefined;
    },
    []
  );

  const loadCompanies = useCallback(async (): Promise<CompanyProps[]> => {
    if (inFlightRef.current.companies) return companiesRef.current;

    inFlightRef.current.companies = true;
    setLoadingCompanies(true);

    try {
      const res = await axiosInstance.get("/api/cba/companies");
      const companiesData: CompanyProps[] = res.data || [];

      setCompanies(companiesData);
      localStorage.setItem("cba.companies", JSON.stringify(companiesData));
      setSelectedCompany((prev) => prev ?? null);

      return companiesData;
    } catch (e) {
      if (e instanceof AuthNotReadyError) return companiesRef.current; // silent
      console.error("Error loading companies:", e);
      return companiesRef.current;
    } finally {
      inFlightRef.current.companies = false;
      setLoadingCompanies(false);
    }
  }, [setSelectedCompany]);

  const loadDataForRole = useCallback(
    async (roleToLoad: string) => {
      if (!roleToLoad) return;

      // Clear all role-scoped data immediately so stale data isn't visible
      setBonuses([]);
      setCommunities([]);
      setCommunities2([]);
      setAllJobs([]);
      setProjects([]);
      setSelectedJob(null);
      resetCommunitiesInFlight();

      setIsRoleLoading(true);

      try {
        const [loadedCompanies, loadedRegions] =
          await Promise.all([
            loadCompanies(),
            loadRegions(),
            loadBonuses(roleToLoad),
            loadJobs(),
            loadProjects(),
          ]);

        const rId = selectedRegionIdRef.current ?? loadedRegions?.[0]?.regionId;
        const resolvedRegion = loadedRegions?.find(r => r.regionId === rId);

        const matchedCompany = resolvedRegion
          ? findCompanyByRegion(loadedCompanies, resolvedRegion.regionId)
          : undefined;

        if (matchedCompany) {
          setSelectedCompany(matchedCompany);
        }

        const cId = matchedCompany?.companyId
          ?? selectedCompanyIdRef.current
          ?? loadedCompanies?.[0]?.companyId;

        // console.log('[loadDataForRole]', { cId, rId, matchedCompany: matchedCompany?.companyId });

        if (typeof cId === "number" && typeof rId === "number") {
          await loadCommunities(cId, rId);
        }
      } finally {
        setIsRoleLoading(false);
      }
    },
    [
      resetCommunitiesInFlight,
      loadBonuses,
      loadRegions,
      loadCompanies,
      loadJobs,
      loadProjects,
      loadCommunities,
    ]
  );

  const loadJobsByProject = useCallback(() => {
    console.warn("loadJobsByProject is not yet implemented");
  }, []);

  // ── Persistent role + bonusType setters ───────────────────────────────────

  const setRoleAndPersist = useCallback((nextRole: string | null) => {
    _setRole((prev) => {
      if (prev === nextRole) return prev;
      lastLoadedRoleRef.current = null;
      if (nextRole) localStorage.setItem(ROLE_KEY, nextRole);
      else localStorage.removeItem(ROLE_KEY);
      return nextRole;
    });
  }, []);

  const setBonusTypeAndPersist = useCallback(
    async (type: string) => {
      const next = type || DEFAULT_BONUS_TYPE;
      const prev = bonusTypeRef.current;
      if (prev === next) return;

      _setBonusType(next);
      localStorage.setItem(BONUS_TYPE_KEY, next);
      bonusTypeRef.current = next;
      setIsBonusTypeLoading(true);

      try {
        const r = roleRef.current;
        await Promise.all([
          r ? loadBonuses(r) : Promise.resolve(),
          loadJobs(),
          loadProjects(),
        ]);
      } finally {
        setIsBonusTypeLoading(false);
      }
    },
    [loadBonuses, loadJobs, loadProjects]
  );

  const syncBonusTypeFromStorage = useCallback((type: string) => {
    const next = type || DEFAULT_BONUS_TYPE;
    if (bonusTypeRef.current === next) return;
    _setBonusType(next);
    bonusTypeRef.current = next;
    // intentionally no re-fetches — just sync the UI state
  }, []);

  // ── Region update ─────────────────────────────────────────────────────────

  const updateSelectedRegion = useCallback(
    async (region: RegionProps) => {
      setSelectedRegion(region); // optimistic
      setIsRegionLoading(true);

      try {
        const latestCompanies = await loadCompanies();
        const matchedCompany = findCompanyByRegion(latestCompanies, region.regionId);

        if (matchedCompany) setSelectedCompany(matchedCompany);

        await Promise.all([
          matchedCompany?.companyId
            ? loadCommunities(matchedCompany.companyId, region.regionId)
            : Promise.resolve(),
          loadProjects(),
          // Now properly awaited — spinner covers the full operation
          roleRef.current ? loadBonuses(roleRef.current) : Promise.resolve(),
          loadJobs(),
        ]);
      } finally {
        setIsRegionLoading(false);
      }
    },
    [
      loadCompanies,
      findCompanyByRegion,
      setSelectedRegion,
      setSelectedCompany,
      loadProjects,
      loadCommunities,
      loadBonuses,
      loadJobs,
    ]
  );

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!role || !isMsalReady) return;
    if (lastLoadedRoleRef.current === role) return;
    lastLoadedRoleRef.current = role;
    loadDataForRole(role);
  }, [role, isMsalReady, loadDataForRole]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== BONUS_TYPE_KEY) return;
      syncBonusTypeFromStorage(e.newValue || DEFAULT_BONUS_TYPE);
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [syncBonusTypeFromStorage]);

  // ── Context value ─────────────────────────────────────────────────────────

  return (
    <DataContext.Provider
      value={{
        regions,
        bonuses,
        isRegionLoading,
        isBonusTypeLoading,
        isRoleLoading,
        loadingBonuses,
        loadingCompanies,
        loadingCommunities,
        companies,
        communities,
        communities2,
        allJobs,
        selectedRegion,
        selectedCompany,
        selectedCommunity,
        selectedJob,
        role,
        projects,

        setRole: setRoleAndPersist,
        setSelectedRegion,
        setSelectedCompany,
        setSelectedCommunity,
        setSelectedJob,

        loadBonuses,
        loadRegions,
        loadCompanies,
        loadJobs,
        loadCommunities,
        loadProjects,
        updateSelectedRegion,

        loadJobsByProject,
        loadDataForRole,

        bonusType,
        setBonusType: setBonusTypeAndPersist,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

// ── useData — throws if used outside provider ─────────────────────────────────

export const useData = (): DataContextType => {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useData must be used within a DataProvider");
  return ctx;
};